import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

import tajawalFont from "../assets/font/Tajawal-Regular.ttf";
import { color } from "framer-motion";

Font.register({
  family: "Tajawal",
  fonts: [
    {
      src: tajawalFont,
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontSize: 10,
    fontFamily: "Tajawal",
  },
  arabicText: {
    fontFamily: "Tajawal",
    textAlign: "right",
    direction: "rtl",
  },
  englishText: {
    fontFamily: "Tajawal",
    textAlign: "left",
    direction: "ltr",
  },
  header: {
    textAlign: "center",
    fontSize: 24,
    fontWeight: "bold",
    color: "#79329a",
    marginBottom: 5,
    paddingBottom: 5,
  },
  line: {
    borderBottomWidth: 2,
    borderBottomColor: "#79329a",
    marginBottom: 30,
  },
  studentSection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  studentTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#1f2937",
  },
  studentDetails: {
    flexDirection: "column",
    gap: 5,
  },
  studentRow: {
    flexDirection: "row",
    marginBottom: 3,
    fontSize: 10,
  },
  studentRowRTL: {
    flexDirection: "row-reverse",
    marginBottom: 3,
    fontSize: 10,
  },
  studentLabel: {
    fontWeight: "bold",
    marginRight: 8,
    width: 80,
  },
  studentLabelRTL: {
    fontWeight: "bold",
    marginLeft: 8,
    width: 80,
    textAlign: "right",
  },
  dateSection: {
    textAlign: "center",
    fontSize: 12,
    marginBottom: 20,
    color: "#6b7280",
  },
  tableContainer: {
    marginBottom: 20,
  },
  table: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    marginBottom: 20,
  },
  tableWrapper: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    minPresenceAhead: 100,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableHeaderRTL: {
    flexDirection: "row-reverse",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowRTL: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    orphans: 3,
    widows: 3,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },
  tableCellLast: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
  },
  tableCellClass: {
    flexDirection: "column",
    alignContent: "center",
    alignItems: "center",
    gap: 5,
  },
  transactionName: {
    fontSize: 10,
    fontWeight: "bold",
  },
  cellClassName: {
    fontSize: 10,
    color: "#6b7280",
  },
  summarySection: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 20,
    gap: 10,
  },
  summaryBox: {
    flex: 1,
    padding: 15,
    borderRadius: 5,
    textAlign: "center",
  },
  balanceBox: {
    backgroundColor: "#ddecf6",
    borderWidth: 1,
    borderColor: "#426f8c",
  },
  boxTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  boxAmount: {
    fontSize: 16,
    fontWeight: "bold",
  },
  balanceAmount: {
    color: "#000000",
  },
  positiveAmount: {
    color: "#16a34a",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const StudentTransactionStatementPDF = ({
  language = "EN",
  companyName = "Your Company Name",
  data = {},
  transactions = [],
  schoolName = "",
}) => {
  const isArabic = language === "AR";

  // Configuration for pagination
  const RECORDS_PER_PAGE = 15; // Adjust this number based on your needs

  // Helper function to split transactions into pages
  const paginateTransactions = (transactions) => {
    const pages = [];
    for (let i = 0; i < transactions.length; i += RECORDS_PER_PAGE) {
      pages.push(transactions.slice(i, i + RECORDS_PER_PAGE));
    }
    return pages.length > 0 ? pages : [[]]; // Ensure at least one page
  };

  // Translations
  const translations = {
    EN: {
      studentDetails: "Student Details",
      studentName: "Name",
      studentClass: "Class",
      studentId: "Student ID",
      currentDate: "Statement Date",
      payment: "Payment",
      transactionType: "Transaction Type",
      date: "Date",
      amount: "Amount",
      balance: "Balance",
      schoolName: "School Name",
      transactionReport: "Student Payment Statement",
      academicLevel: "Academic Level",
      registrationFee: "Registration Fee",
      installmentPayment: "Installment Payment",
    },
    AR: {
      schoolName: "إسم المدرسة",
      studentDetails: "بيانات الطالب",
      studentName: "الاسم",
      studentClass: "الصف",
      studentId: "رقم الطالب",
      currentDate: "تاريخ الكشف",
      payment: "الدفع",
      transactionType: "نوع الحركة",
      date: "التاريخ",
      amount: "المبلغ",
      balance: "الرصيد",
      alhabbarSlogan: "شركة الحبّار للتطوير البرمجي",
      ehsiblySecondSlogan: "برنامج إحسبلي للإدارة والمحاسبة",
      transactionReport: "كشف دفعات الطالب",
      academicLevel: "المستوى الأكاديمي",
      registrationFee: "رسم التسجيل",
      installmentPayment: "دفعة قسط",
    },
  };

  const t = translations[language] || translations.EN;

  // Extract data from props
  const student = data.student || {};
  const paymentsData = data.payments || [];

  // Student data
  const studentData = {
    name: student.fullName || (isArabic ? "اسم الطالب" : "Student Name"),
    class:
      student.activeYear?.schoolClass?.name || (isArabic ? "الصف" : "Class"),
    studentId: student.studentId || student.id || "N/A",
    studentCode: student.studentCode,
    academicLevel:
      student.academicLevel ||
      (isArabic ? "المستوى الأكاديمي" : "Academic Level"),
  };

  // Summary data
  const summary = {
    balance: data.balance || 0,
  };

  // Get currency (assuming IQD for students)
  const currency = "IQD";

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount) + ` ${currency}`;
  };

  // Format date from ISO string
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-UK");
  };

  // Get current date
  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-UK");
  };

  // Helper component for styled text
  const StyledText = ({ children, style = {} }) => {
    const baseStyle = isArabic ? styles.arabicText : styles.englishText;
    return <Text style={{ ...baseStyle, ...style }}>{children}</Text>;
  };

  // Get paginated transactions
  const paginatedTransactions = paginateTransactions(transactions);

  return (
    <Document>
      {paginatedTransactions.map((pageTransactions, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* Header - Company Name - Show on all pages */}
          <View style={styles.line}>
            <StyledText style={styles.header}>{companyName}</StyledText>
            <StyledText style={styles.dateSection}>
              {t.transactionReport}
            </StyledText>
          </View>

          {/* Student Details Section - Only show on first page */}
          {pageIndex === 0 && (
            <View style={styles.studentSection}>
              <StyledText style={styles.studentTitle}>
                {t.studentDetails}
              </StyledText>
              <View style={styles.studentDetails}>
                <View
                  style={isArabic ? styles.studentRowRTL : styles.studentRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.studentLabelRTL : styles.studentLabel
                    }
                  >
                    {t.studentName}:
                  </StyledText>
                  <StyledText>{studentData.name}</StyledText>
                </View>
                <View
                  style={isArabic ? styles.studentRowRTL : styles.studentRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.studentLabelRTL : styles.studentLabel
                    }
                  >
                    {t.studentClass}:
                  </StyledText>
                  <StyledText>{studentData.class}</StyledText>
                </View>
                <View
                  style={isArabic ? styles.studentRowRTL : styles.studentRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.studentLabelRTL : styles.studentLabel
                    }
                  >
                    {t.studentId}:
                  </StyledText>
                  <StyledText>{studentData.studentCode}</StyledText>
                </View>
              </View>
            </View>
          )}

          {/* Current Date - Show on all pages */}
          <View style={styles.flexRow}>
            <StyledText style={styles.dateSection}>
              {t.schoolName}: {schoolName}
            </StyledText>
            <StyledText style={styles.dateSection}>
              {t.currentDate}: {getCurrentDate()}
            </StyledText>
          </View>

          {/* Payments Table */}
          <View style={styles.tableContainer}>
            <View style={styles.tableWrapper}>
              {/* Table Header - Show on every page */}
              <View
                style={isArabic ? styles.tableHeaderRTL : styles.tableHeader}
              >
                <StyledText style={styles.tableCell}>
                  {t.transactionType}
                </StyledText>
                <StyledText style={styles.tableCell}>{t.date}</StyledText>
                <StyledText style={styles.tableCellLast}>{t.amount}</StyledText>
              </View>

              {/* Table Rows for this page */}
              {pageTransactions.map((payment, index) => (
                <View
                  key={index}
                  style={[
                    isArabic ? styles.tableRowRTL : styles.tableRow,
                    styles.tableRowContainer,
                  ]}
                >
                  {isArabic ? (
                    <>
                      (
                      {payment.type !== "yearDept" && (
                        <StyledText
                          style={{
                            ...styles.tableCell,
                            ...(payment.amount < 0
                              ? styles.balanceAmount
                              : styles.positiveAmount),
                          }}
                        >
                          {formatCurrency(Math.abs(payment.amount))}
                        </StyledText>
                      )}
                      )
                      {payment.type === "yearDept" && (
                        <StyledText
                          style={{
                            ...styles.tableCell,
                            color: "#dc2626",
                          }}
                        >
                          {formatCurrency(payment.amount)}
                        </StyledText>
                      )}
                      <StyledText style={styles.tableCell}>
                        {formatDate(payment.date)}
                      </StyledText>
                      <View
                        style={[styles.tableCellLast, styles.tableCellClass]}
                      >
                        <StyledText style={styles.transactionName}>
                          {payment.transactionName ||
                            payment.type ||
                            t.installmentPayment}
                        </StyledText>
                        <StyledText style={styles.cellClassName}>
                          {payment.class.name}
                        </StyledText>
                      </View>
                    </>
                  ) : (
                    <>
                      <View
                        style={[styles.tableCellLast, styles.tableCellClass]}
                      >
                        <StyledText style={styles.transactionName}>
                          {payment.transactionName ||
                            payment.type ||
                            t.installmentPayment}
                        </StyledText>
                        {/* <StyledText style={styles.cellClassName}>
                        {payment.class.name}
                      </StyledText> */}
                      </View>
                      <StyledText style={styles.tableCell}>
                        {formatDate(payment.date)}
                      </StyledText>
                      <StyledText style={styles.tableCellLast}>
                        {formatCurrency(payment.amount)}
                      </StyledText>
                    </>
                  )}
                </View>
              ))}
            </View>
          </View>

          {/* Summary Box - Only show on last page */}
          {pageIndex === paginatedTransactions.length - 1 && (
            <View style={styles.summarySection}>
              <View style={[styles.summaryBox, styles.balanceBox]}>
                <StyledText style={styles.boxTitle}>{t.balance}</StyledText>
                <StyledText style={[styles.boxAmount, styles.balanceAmount]}>
                  {formatCurrency(summary.balance)}
                </StyledText>
              </View>
            </View>
          )}

          {/* Footer Section - Show on all pages */}
          <View
            style={{
              position: "absolute",
              bottom: 20,
              left: 20,
              right: 20,
              textAlign: "center",
              paddingTop: 10,
              borderTopWidth: 1,
              borderTopColor: "#d1d5db",
              flexDirection: isArabic ? "column-reverse" : "column",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: 8,
                fontFamily: "Tajawal",
              }}
            >
              {t.alhabbarSlogan}
            </Text>
            <Text
              style={{
                fontSize: 10,
                fontWeight: "bold",
                marginBottom: 4,
                fontFamily: "Tajawal",
              }}
            >
              {t.ehsiblySecondSlogan}
            </Text>

            {/* Page number if multiple pages */}
            {paginatedTransactions.length > 1 && (
              <Text
                style={{
                  fontSize: 8,
                  fontFamily: "Tajawal",
                  marginTop: 5,
                  color: "#6b7280",
                }}
              >
                Page {pageIndex + 1} of {paginatedTransactions.length}
              </Text>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default StudentTransactionStatementPDF;
